import { groupBy } from "lodash";
import { SelectOption } from "../select/virtualized-select";

const filterOptions = (options: SelectOption[], value: string) => {
  return options.filter((option) => {
    return option.label.toLocaleUpperCase().includes(value.toLocaleUpperCase());
  });
};

const flattenOptionGroups = (rows: SelectOption[]) => {
  const collectGroups = groupBy(rows, (o) => o.group ?? "ungrouped");
  const flatRows: SelectOption[] = [];
  Object.keys(collectGroups).forEach((group) => {
    if (group === "ungrouped") {
      flatRows.push(...collectGroups[group]);
    } else {
      // If we have a group we are pushing a header option to the top of the list
      flatRows.push({
        label: group,
        isGroupHeader: true,
        value: group,
      });
      flatRows.push(...collectGroups[group]);
    }
  });

  return flatRows;
};

type ReactWindowList = {
  scrollToItem: (index: number) => void;
};

const useVirtualizedDownshiftStateChange = (opts: {
  listRef: React.RefObject<ReactWindowList>;
  onSelectItem: (item: SelectOption) => void;
  setInputValue: (value: string) => void;
}) => {
  const handleStateChange = (changes) => {
    if (changes.type === "__autocomplete_keydown_enter__") {
      opts.onSelectItem(changes.selectedItem);
    }
    if (changes.type === "__autocomplete_change_input__") {
      opts.setInputValue(changes.inputValue ?? "");
    }
    if (changes.hasOwnProperty("highlightedIndex")) {
      if (opts.listRef.current) {
        opts.listRef.current.scrollToItem(changes.highlightedIndex);
      }
    }
  };

  return handleStateChange;
};

export const useVirtualizedDownshiftStateReducer = (opts: {
  options: SelectOption[];
}) => {
  return (state, actionAndChanges) => {
    if (actionAndChanges.type === "__autocomplete_keydown_arrow_down__") {
      const item = opts.options[actionAndChanges.highlightedIndex];
      if (item && item.isGroupHeader) {
        return {
          ...state,
          highlightedIndex: actionAndChanges.highlightedIndex + 1,
        };
      }
    }

    if (actionAndChanges.type === "__autocomplete_keydown_arrow_up__") {
      const item = opts.options[actionAndChanges.highlightedIndex];
      if (item.isGroupHeader) {
        return {
          ...state,
          highlightedIndex: actionAndChanges.highlightedIndex - 1,
        };
      }
    }

    // We need to make sure we don't close the popover or scroll to the top of the list
    // when the user selects an item using the enter key
    if (actionAndChanges.type === "__autocomplete_keydown_enter__") {
      return {
        ...actionAndChanges,
        highlightedIndex: state.highlightedIndex, // use the last highlighted index
      };
    }
    return actionAndChanges;
  };
};
